import React from "react";
import { Link } from "react-router-dom";
import { useBranding } from "@/context/BrandingContext";
import { resolveTenantLogo, validLogoScale } from "@/lib/logoResolver";

/**
 * Brand Logo — Unified Tenant Logo Resolver (single source of truth).
 * Precedence di SEMUA area: logo_horizontal → logo → mark → tenant initial.
 *
 * Variants:
 *   - "mark"        : icon only (square)
 *   - "horizontal"  : icon + brand name + tagline
 *   - "compact"     : icon + name (no tagline)
 *   - "admin"       : logo tenant + label ADMIN (logo_horizontal juga menang di sini)
 *   - "stacked"     : icon centered above name (for auth pages)
 */
export default function Logo({
  variant = "compact",
  size = "md",
  to = "/",
  className = "",
  invert = false,
  showTagline = true,
}) {
  const { branding } = useBranding();
  const [hLogoFailed, setHLogoFailed] = React.useState(false);
  const [iconFailed, setIconFailed] = React.useState(false);
  const name = branding.general.name;
  const tagline = branding.general.tagline;
  const resolved = resolveTenantLogo(branding.visual, name);
  const logoScale = validLogoScale(branding.visual?.logo_scale);

  const sizes = {
    sm: { icon: 28, title: "text-base", sub: "text-[9px]", adminImg: "h-7 max-w-[150px]" },
    md: { icon: 36, title: "text-lg", sub: "text-[10px]", adminImg: "h-9 max-w-[210px]" },
    lg: { icon: 44, title: "text-xl", sub: "text-[10px]", adminImg: "h-10 max-w-[240px]" },
    xl: { icon: 56, title: "text-2xl", sub: "text-[11px]", adminImg: "h-11 max-w-[260px]" },
  };
  const s = sizes[size] || sizes.md;

  // Logo horizontal menang di SEMUA area — termasuk admin. Contain, no-stretch,
  // no-crop; tinggi & lebar admin dibatasi agar header tetap rapi.
  if (resolved.type === "horizontal" && !hLogoFailed) {
    if (variant === "admin") {
      const admin = (
        <span className={`inline-flex items-center gap-2 min-w-0 ${className}`} data-brand-name={name}>
          <img
            src={resolved.src}
            alt={`${name} logo`}
            onError={() => setHLogoFailed(true)}
            className={`w-auto shrink object-contain object-left ${s.adminImg}`}
            data-testid="brand-logo-horizontal-admin"
          />
          <span className="shrink-0 text-[9px] uppercase tracking-[0.16em] font-bold text-neutral-500 border border-neutral-300 rounded px-1 py-0.5">
            ADMIN
          </span>
        </span>
      );
      if (!to) return admin;
      return <Link to={to} className="inline-flex items-center min-w-0" data-testid="brand-logo">{admin}</Link>;
    }
    const img = (
      <img
        src={resolved.src}
        alt={`${name} logo`}
        onError={() => setHLogoFailed(true)}
        className={`storefront-logo-h ${className}`}
        data-logo-scale={logoScale}
        data-testid="brand-logo-horizontal"
      />
    );
    if (!to) return img;
    return <Link to={to} className="inline-flex items-center" data-testid="brand-logo">{img}</Link>;
  }

  const titleColor = invert ? "text-white" : "text-[color:var(--brand-secondary)]";
  const subColor = invert ? "text-white/70" : "text-[color:var(--brand-primary)]";

  const iconEl = (resolved.type === "initial" || iconFailed) ? (
    <span
      className="shrink-0 grid place-items-center rounded-lg font-extrabold text-white"
      style={{ width: s.icon, height: s.icon, backgroundColor: "var(--brand-primary)", fontSize: s.icon * 0.5 }}
      data-testid="brand-logo-initial"
    >
      {resolved.initial || (name || "?").trim().charAt(0).toUpperCase()}
    </span>
  ) : (
    <img
      src={resolved.src}
      alt={name}
      width={s.icon}
      height={s.icon}
      onError={() => setIconFailed(true)}
      className="shrink-0 object-contain"
      style={{ width: s.icon, height: s.icon }}
    />
  );

  const inner = (
    <span className={`inline-flex items-center gap-2 ${className}`} data-brand-name={name}>
      {iconEl}
      {variant !== "mark" && (
        <span className="flex flex-col leading-tight">
          <span className={`font-display font-extrabold tracking-tight ${s.title} ${titleColor}`}>
            {name}
          </span>
          {showTagline && (
            <span className={`uppercase tracking-[0.18em] font-bold ${s.sub} ${subColor}`}>
              {variant === "admin" ? "ADMIN" : tagline}
            </span>
          )}
        </span>
      )}
    </span>
  );

  if (!to) return inner;
  return <Link to={to} className="inline-flex items-center" data-testid="brand-logo">{inner}</Link>;
}

export function LogoStacked({ size = "lg", invert = false }) {
  const { branding } = useBranding();
  const [hLogoFailed, setHLogoFailed] = React.useState(false);
  const [iconFailed, setIconFailed] = React.useState(false);
  const name = branding.general.name;
  const tagline = branding.general.tagline;
  const resolved = resolveTenantLogo(branding.visual, name);
  const logoScale = validLogoScale(branding.visual?.logo_scale);

  // Unified resolver — logo horizontal jadi identitas utama halaman auth;
  // nama tenant & tagline TIDAK dirender terpisah (perilaku sama dengan storefront).
  if (resolved.type === "horizontal" && !hLogoFailed) {
    return (
      <Link to="/" className="flex items-center justify-center" data-testid="brand-logo-stacked">
        <img
          src={resolved.src}
          alt={`${name} logo`}
          onError={() => setHLogoFailed(true)}
          className="storefront-logo-h"
          data-logo-scale={logoScale}
          data-testid="brand-logo-horizontal-auth"
        />
      </Link>
    );
  }

  const s = size === "xl" ? 64 : size === "lg" ? 56 : 48;
  const iconEl = (resolved.type === "initial" || iconFailed) ? (
    <span
      className="shrink-0 grid place-items-center rounded-xl font-extrabold text-white"
      style={{ width: s, height: s, backgroundColor: "var(--brand-primary)", fontSize: s * 0.5 }}
      data-testid="brand-logo-initial"
    >
      {resolved.initial || (name || "?").trim().charAt(0).toUpperCase()}
    </span>
  ) : (
    <img
      src={resolved.src}
      alt={name}
      width={s}
      height={s}
      onError={() => setIconFailed(true)}
      className="shrink-0 object-contain"
      style={{ width: s, height: s }}
    />
  );

  return (
    <Link to="/" className="flex items-center justify-center flex-col gap-2" data-testid="brand-logo-stacked">
      {iconEl}
      <div className="text-center">
        <div className={`font-display font-extrabold leading-none ${invert ? "text-white" : "text-[color:var(--brand-secondary)]"} text-2xl`}>
          {name}
        </div>
        <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mt-1 ${invert ? "text-white/70" : "text-[color:var(--brand-primary)]"}`}>
          {tagline}
        </div>
      </div>
    </Link>
  );
}
